import React from "react";
import * as ModalPrimitive from "@radix-ui/react-dialog";
import { ModalProps, ModalSize, ModalNamespace } from "./Modal.types";
import { Overlay } from "./Overlay.styles";
import { Content } from "./Content.styles";
import { CloseBtn } from "./Close.styles";

/**
 * Determine the size of the modal based on size prop or custom width
 * @param size - The predefined size option
 * @param width - The width value to use for custom size
 */
const setSize = (size: string | undefined, width: string | number) => {
  switch (size) {
    case ModalSize.custom:
      return width;
    case ModalSize.large:
      return 900;
    case ModalSize.medium:
      return 600;
    case ModalSize.small:
    default:
      return 480;
  }
};

/**
 * Modal component
 *
 * A dialog component that follows WAI-ARIA Dialog (Modal) pattern.
 * Built on Radix UI Dialog primitives for accessibility.
 */
export const Modal = Object.assign(
  // Main component implementation
  ({
    children,
    isOpen,
    onOpenChange,
    hasOverlay = true,
    size,
    width = "95%",
    height,
    id,
    className,
    handleClose,
    autoFocus = false,
    modal = true,
    ...props
  }: ModalProps) => (
    <ModalPrimitive.Root
      {...props}
      open={isOpen}
      modal={modal}
      onOpenChange={onOpenChange}
    >
      <ModalPrimitive.Portal>
        {hasOverlay && <Overlay />}
        <Content
          width={setSize(size, width)}
          height={height}
          onOpenAutoFocus={e => (autoFocus ? true : e.preventDefault())}
          onPointerDownOutside={open => onOpenChange?.(open)}
          className={className}
          onKeyDown={e => {
            if (e.key === "Escape") {
              onOpenChange?.(false);
              handleClose?.();
            }
          }}
          id={id}
        >
          <ModalPrimitive.Close asChild>
            <CloseBtn
              onClick={open => {
                onOpenChange?.(open);
                handleClose?.();
              }}
            />
          </ModalPrimitive.Close>
          {children}
        </Content>
      </ModalPrimitive.Portal>
    </ModalPrimitive.Root>
  ),
  // Subcomponents
  {
    Root: ModalPrimitive.Root,
    Trigger: ModalPrimitive.Trigger,
    Portal: ModalPrimitive.Portal,
    Close: ModalPrimitive.Close,
    Overlay,
    Content,
    CloseButton: CloseBtn
  }
) as React.FC<ModalProps> & ModalNamespace;
